// Imperative Leaflet map. React owns the data; this component mirrors it
// into Leaflet layers (markers, rings, tracks, overlays) on every change.
// Pane layout follows wxchange-demo-viewer: rasters under labels, vectors on top.

import { useEffect, useRef } from "react";
import L from "leaflet";
import { basemapLayers } from "../lib/basemap.js";
import { MARKER } from "../lib/palettes.js";
import { stationTooltip } from "../lib/observations.js";
import { hurricaneTrack, previewRadiusKm } from "../lib/scenario.js";

function markerIcon(type, selected) {
  const m = MARKER[type] || { color: "#888", glyph: "?" };
  return L.divIcon({
    className: "",
    iconSize: [34, 34],
    iconAnchor: [17, 17],
    html: `<div class="chaos-pin${selected ? " sel" : ""}" style="--pin:${m.color}">${m.glyph}</div>`,
  });
}

export default function MapView({ config, scenarios, selectedId, placing, horizon, overlay, alerts, observations, onPlace, onMove, onSelect }) {
  const el = useRef(null);
  const map = useRef(null);
  const layers = useRef({});
  const cb = useRef({ onPlace, onMove, onSelect, placing });
  cb.current = { onPlace, onMove, onSelect, placing };

  // Map init (once).
  useEffect(() => {
    const m = L.map(el.current, { zoomControl: false, minZoom: 2, maxZoom: 10, worldCopyJump: true, fadeAnimation: false }).setView(config.map.center, config.map.zoom);
    m.createPane("wxRaster").style.zIndex = 250;
    const labels = m.createPane("wxLabels");
    labels.style.zIndex = 300;
    labels.style.pointerEvents = "none";
    m.createPane("wxVector").style.zIndex = 400;
    const b = basemapLayers("dark");
    L.tileLayer(b.base, { attribution: b.attribution, maxZoom: 16 }).addTo(m);
    L.tileLayer(b.labels, { pane: "wxLabels", maxZoom: 16 }).addTo(m);
    L.control.zoom({ position: "topright" }).addTo(m);
    m.on("click", (e) => {
      if (cb.current.placing) cb.current.onPlace(e.latlng.lat, e.latlng.lng);
    });
    layers.current = {
      scenarios: L.layerGroup().addTo(m),
      alerts: L.layerGroup().addTo(m),
      obs: L.layerGroup().addTo(m),
      raster: null,
    };
    map.current = m;
    const ro = new ResizeObserver(() => m.invalidateSize());
    ro.observe(el.current);
    return () => {
      ro.disconnect();
      m.remove();
      map.current = null;
    };
  }, [config]);

  useEffect(() => {
    if (el.current) el.current.style.cursor = placing ? "crosshair" : "";
  }, [placing]);

  // Placed scenarios: marker, reach ring, hurricane track.
  useEffect(() => {
    const g = layers.current.scenarios;
    if (!g) return;
    g.clearLayers();
    for (const s of scenarios) {
      const color = (MARKER[s.type] || {}).color || "#888";
      const sel = s.id === selectedId;
      L.circle([s.lat, s.lon], { radius: previewRadiusKm(s) * 1000, pane: "wxVector", color, weight: sel ? 2 : 1, opacity: 0.9, fillOpacity: sel ? 0.12 : 0.06, dashArray: "4 4", interactive: false }).addTo(g);
      if (s.type === "hurricane") {
        L.polyline(hurricaneTrack(s, horizon), { pane: "wxVector", color, weight: 2, opacity: 0.8, dashArray: "2 6", interactive: false }).addTo(g);
      }
      const mk = L.marker([s.lat, s.lon], { icon: markerIcon(s.type, sel), draggable: true, pane: "wxVector", autoPan: true, keyboard: false });
      mk.on("click", () => cb.current.onSelect(s.id));
      mk.on("dragend", (e) => {
        const ll = e.target.getLatLng();
        cb.current.onMove(s.id, ll.lat, ll.lng);
      });
      mk.addTo(g);
    }
  }, [scenarios, selectedId, horizon]);

  // Field preview raster. The new overlay goes in before the old one is
  // removed, so scrubbing never flashes an empty map.
  useEffect(() => {
    const m = map.current;
    if (!m) return;
    const prev = layers.current.raster;
    if (overlay) {
      const next = L.imageOverlay(overlay.url, overlay.bounds, { pane: "wxRaster", opacity: 0.8, interactive: false }).addTo(m);
      layers.current.raster = next;
    } else {
      layers.current.raster = null;
    }
    if (prev) prev.remove();
  }, [overlay]);


  useEffect(() => {
    const g = layers.current.alerts;
    if (!g) return;
    g.clearLayers();
    if (!alerts) return;
    L.geoJSON(alerts, {
      pane: "wxVector",
      style: (f) => ({ color: f.properties.severity === "Extreme" ? "#ff4d6d" : "#ffb224", weight: 2, fillOpacity: 0.05 }),
      onEachFeature: (f, layer) => layer.bindTooltip(`<b>${f.properties.event}</b><br/>${f.properties.areaDesc}`, { className: "wx-stn-tip", sticky: true }),
    }).addTo(g);
  }, [alerts]);

  useEffect(() => {
    const g = layers.current.obs;
    if (!g) return;
    g.clearLayers();
    for (const f of observations || []) {
      const [lon, lat] = f.geometry.coordinates;
      L.circleMarker([lat, lon], { pane: "wxVector", radius: 4, color: "#e6edf3", weight: 1, fillColor: "#3b82f6", fillOpacity: 0.9 })
        .bindTooltip(stationTooltip(f.properties), { className: "wx-stn-tip" })
        .addTo(g);
    }
  }, [observations]);


  return <div ref={el} className="chaos-map" />;
}
